// Service-area map. The block prints the shape, marker and tile source as data; Leaflet
// itself only starts once the map is near the screen, so it costs nothing above the fold.
type Shape = {
	points: [number, number][];
	stroke: { color: string; weight: number; opacity: number };
	fill: { color: string; opacity: number };
};

type MapData = {
	center: [number, number];
	zoom: number;
	shapes: Shape[];
	markers: { lat: number; lng: number; title: string }[];
	tiles: { url: string; subdomains: string; attribution: string };
};

declare const L: any;

function draw(host: HTMLElement, data: MapData): void {
	const map = L.map(host, {
		center: data.center,
		zoom: data.zoom,
		// A map inside a long page should not swallow the scroll.
		scrollWheelZoom: false,
		attributionControl: true,
	});

	L.tileLayer(data.tiles.url, {
		subdomains: data.tiles.subdomains || 'abc',
		attribution: data.tiles.attribution,
		maxZoom: 19,
	}).addTo(map);

	data.shapes.forEach((shape) => {
		L.polygon(shape.points, {
			color: shape.stroke.color,
			weight: shape.stroke.weight,
			opacity: shape.stroke.opacity,
			fillColor: shape.fill.color,
			fillOpacity: shape.fill.opacity,
		}).addTo(map);
	});

	data.markers.forEach((marker) => {
		const pin = L.marker([marker.lat, marker.lng], {
			icon: L.divIcon({
				className: 'armada-areas__pin',
				html: '<span></span>',
				iconSize: [18, 18],
				iconAnchor: [9, 9],
			}),
			title: marker.title,
			keyboard: false,
		}).addTo(map);

		if (marker.title) {
			// Titles come from the plugin's table, so they go in as text, never markup.
			const label = document.createElement('span');

			label.textContent = marker.title;
			pin.bindPopup(label);
		}
	});

	host.classList.add('is-ready');
}

function start(host: HTMLElement): void {
	let data: MapData;

	try {
		data = JSON.parse(host.dataset.armadaMap || '');
	} catch {
		return;
	}

	const run = (): void => {
		if (typeof L === 'undefined') {
			// The script is deferred; wait for it rather than giving up on the map.
			window.setTimeout(run, 120);

			return;
		}

		draw(host, data);
	};

	run();
}

export default function initAreasMap(): void {
	const maps = document.querySelectorAll<HTMLElement>('[data-armada-map]');

	if (!maps.length) {
		return;
	}

	if (!('IntersectionObserver' in window)) {
		maps.forEach(start);

		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					observer.unobserve(entry.target);
					start(entry.target as HTMLElement);
				}
			});
		},
		{ rootMargin: '300px 0px' }
	);

	maps.forEach((map) => observer.observe(map));
}
